/**
 * Main App component with routing and global providers
 */
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Toaster } from 'react-hot-toast';

// Pages
import LandingPage from './pages/LandingPage';
import FamilyOfficeLandingPage from './pages/FamilyOfficeLandingPage';
import FundLandingPage from './pages/FundLandingPage';
import InvestorLandingPage from './pages/InvestorLandingPage';
import PartnerLinkPage from './pages/PartnerLinkPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ConnectPage from './pages/ConnectPage';
import SurveyPage from './pages/SurveyPage';
import ConnectResultPage from './pages/ConnectResultPage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import VerifyCodePage from './pages/VerifyCodePage';
import Dashboard from './pages/Dashboard';
import NewAnalysisPage from './pages/NewAnalysisPage';
import AnalysesPage from './pages/AnalysesPage';
import CompaniesPage from './pages/CompaniesPage';
import CompanyDetailPage from './pages/CompanyDetailPage';
import CompanyBrochureUploadPage from './pages/CompanyBrochureUploadPage';
import NewCompanyPage from './pages/NewCompanyPage';
import CompanyDuplicatesPage from './pages/CompanyDuplicatesPage';
import SettingsPage from './pages/SettingsPage';
import DetailedReportPage from './pages/DetailedReportPage';
import SalesFunnelPage from './pages/SalesFunnelPage';
import EngagementsPage from './pages/EngagementsPage';
import LeadsPage from './pages/LeadsPage';
import LeadDetailPage from './pages/LeadDetailPage';
import IntakeChatsPage from './pages/IntakeChatsPage';
import ResearchStatusPage from './pages/ResearchStatusPage';
import MarketingTargetsPage from './pages/MarketingTargetsPage';
import InvestorsPage from './pages/InvestorsPage';
import InvestorDetailPage from './pages/InvestorDetailPage';
import NewInvestorPage from './pages/NewInvestorPage';
import InvestorBrochureUploadPage from './pages/InvestorBrochureUploadPage';
import ProjectsPage from './pages/ProjectsPage';
import NewProjectPage from './pages/NewProjectPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import InstrumentsPage from './pages/InstrumentsPage';
import MatchesPage from './pages/MatchesPage';
import MatchDetailPage from './pages/MatchDetailPage';
import DealBriefsPage from './pages/DealBriefsPage';
import DealsPage from './pages/DealsPage';
import DealPipelinePage from './pages/DealPipelinePage';
import DealPipelineDetailPage from './pages/DealPipelineDetailPage';
import PipelineAnalyticsPage from './pages/PipelineAnalyticsPage';
import DealRoomsPage from './pages/DealRoomsPage';
import PredictionWatchlistPage from './pages/PredictionWatchlistPage';
import NewDealRoomPage from './pages/NewDealRoomPage';
import DealRoomDetailPage from './pages/DealRoomDetailPage';
import DealRoomViewPage from './pages/DealRoomViewPage';
import SyndicationPage from './pages/SyndicationPage';
import NewSyndicationRoundPage from './pages/NewSyndicationRoundPage';
import SyndicationDetailPage from './pages/SyndicationDetailPage';
import WireTrackerPage from './pages/WireTrackerPage';
import CRSDashboardPage from './pages/CRSDashboardPage';
import CRSDetailPage from './pages/CRSDetailPage';
import CRSScorerPage from './pages/CRSScorerPage';
import CRSHistoryPage from './pages/CRSHistoryPage';
import CRSGapAnalysisPage from './pages/CRSGapAnalysisPage';
import ServiceProvidersPage from './pages/ServiceProvidersPage';
import MomentumDashboardPage from './pages/MomentumDashboardPage';
import MomentumDetailPage from './pages/MomentumDetailPage';
import HotDealsPage from './pages/HotDealsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import AISettingsPage from './pages/AISettingsPage';
import TeamPage from './pages/TeamPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import InvestorDashboardPage from './pages/InvestorDashboardPage';
import FounderDashboardPage from './pages/FounderDashboardPage';
import NetworkPartnerDashboardPage from './pages/NetworkPartnerDashboardPage';
import PublicCRSPreviewPage from './pages/PublicCRSPreviewPage';
import ImportsPage from './pages/ImportsPage';
import NewImportPage from './pages/NewImportPage';
import BusinessCardImportPage from './pages/BusinessCardImportPage';
import ImportBatchDetailPage from './pages/ImportBatchDetailPage';
import ClaudeUsagePage from './pages/ClaudeUsagePage';
// import AnalysisResults from './pages/AnalysisResults';
// import CompanyProfile from './pages/CompanyProfile';
// import Settings from './pages/Settings';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

// Store
import { useAuthStore } from './store/authStore';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  const initialize = useAuthStore((state) => state.initialize);
  const isInitializing = useAuthStore((state) => state.isInitializing);

  // Initialize authentication on app start
  useEffect(() => {
    initialize();
  }, [initialize]);

  if (isInitializing) {
    return <LoadingSpinner />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/family-offices" element={<FamilyOfficeLandingPage />} />
            <Route path="/for-funds" element={<FundLandingPage />} />
            <Route path="/for-investors" element={<InvestorLandingPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/connect" element={<ConnectPage />} />
            {/* Public survey runner. Unauthenticated on purpose —
                respondents are client staff with no app account; the
                gate is a work-email domain allowlist enforced server-side. */}
            <Route path="/survey/:surveyKey" element={<SurveyPage />} />
            {/* Public read-only deal-room view for investors + startups.
                Authorized by the ?token= query param, not app login. */}
            <Route path="/deal-room/view" element={<DealRoomViewPage />} />
            <Route path="/connect/result" element={<ConnectResultPage />} />
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/verify-code" element={<VerifyCodePage />} />

            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/new-analysis" element={
              <ProtectedRoute>
                <NewAnalysisPage />
              </ProtectedRoute>
            } />
            <Route path="/analyses" element={
              <ProtectedRoute>
                <AnalysesPage />
              </ProtectedRoute>
            } />
            <Route path="/companies" element={
              <ProtectedRoute>
                <CompaniesPage />
              </ProtectedRoute>
            } />
            <Route path="/companies/upload-brochure" element={
              <ProtectedRoute>
                <CompanyBrochureUploadPage />
              </ProtectedRoute>
            } />
            <Route path="/companies/new" element={
              <ProtectedRoute>
                <NewCompanyPage />
              </ProtectedRoute>
            } />
            <Route path="/companies/duplicates" element={
              <ProtectedRoute>
                <CompanyDuplicatesPage />
              </ProtectedRoute>
            } />
            <Route path="/companies/:id" element={
              <ProtectedRoute>
                <CompanyDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/partner-link" element={
              <ProtectedRoute>
                <PartnerLinkPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/research-status" element={
              <ProtectedRoute>
                <ResearchStatusPage />
              </ProtectedRoute>
            } />
            <Route path="/admin/marketing" element={
              <ProtectedRoute>
                <MarketingTargetsPage />
              </ProtectedRoute>
            } />
            <Route path="/detailed-report/:id" element={
              <ProtectedRoute>
                <DetailedReportPage />
              </ProtectedRoute>
            } />
            <Route path="/sales-funnel" element={
              <ProtectedRoute>
                <SalesFunnelPage />
              </ProtectedRoute>
            } />
            <Route path="/engagements" element={
              <ProtectedRoute>
                <EngagementsPage />
              </ProtectedRoute>
            } />
            <Route path="/leads" element={
              <ProtectedRoute>
                <LeadsPage />
              </ProtectedRoute>
            } />
            <Route path="/leads/:id" element={
              <ProtectedRoute>
                <LeadDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/intake-chats" element={
              <ProtectedRoute>
                <IntakeChatsPage />
              </ProtectedRoute>
            } />
            <Route path="/investors" element={
              <ProtectedRoute>
                <InvestorsPage />
              </ProtectedRoute>
            } />
            <Route path="/investors/new" element={
              <ProtectedRoute>
                <NewInvestorPage />
              </ProtectedRoute>
            } />
            <Route path="/investors/upload-brochure" element={
              <ProtectedRoute>
                <InvestorBrochureUploadPage />
              </ProtectedRoute>
            } />
            <Route path="/investors/:id" element={
              <ProtectedRoute>
                <InvestorDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute>
                <ProjectsPage />
              </ProtectedRoute>
            } />
            <Route path="/projects/new" element={
              <ProtectedRoute>
                <NewProjectPage />
              </ProtectedRoute>
            } />
            <Route path="/projects/:id" element={
              <ProtectedRoute>
                <ProjectDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/instruments" element={
              <ProtectedRoute>
                <InstrumentsPage />
              </ProtectedRoute>
            } />
            <Route path="/matches" element={
              <ProtectedRoute>
                <MatchesPage />
              </ProtectedRoute>
            } />
            <Route path="/matches/:id" element={
              <ProtectedRoute>
                <MatchDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/deals" element={
              <ProtectedRoute>
                <DealsPage />
              </ProtectedRoute>
            } />
            <Route path="/deals/briefs" element={
              <ProtectedRoute>
                <DealBriefsPage />
              </ProtectedRoute>
            } />
            <Route path="/deal-pipeline" element={
              <ProtectedRoute>
                <DealPipelinePage />
              </ProtectedRoute>
            } />
            <Route path="/deal-pipeline/analytics" element={
              <ProtectedRoute>
                <PipelineAnalyticsPage />
              </ProtectedRoute>
            } />
            <Route path="/deal-pipeline/:id" element={
              <ProtectedRoute>
                <DealPipelineDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/deal-rooms" element={
              <ProtectedRoute>
                <DealRoomsPage />
              </ProtectedRoute>
            } />
            <Route path="/prediction-watchlist" element={
              <ProtectedRoute>
                <PredictionWatchlistPage />
              </ProtectedRoute>
            } />
            <Route path="/deal-rooms/new" element={
              <ProtectedRoute>
                <NewDealRoomPage />
              </ProtectedRoute>
            } />
            <Route path="/deal-rooms/:id" element={
              <ProtectedRoute>
                <DealRoomDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/syndication" element={
              <ProtectedRoute>
                <SyndicationPage />
              </ProtectedRoute>
            } />
            <Route path="/syndication/new" element={
              <ProtectedRoute>
                <NewSyndicationRoundPage />
              </ProtectedRoute>
            } />
            <Route path="/syndication/wires" element={
              <ProtectedRoute>
                <WireTrackerPage />
              </ProtectedRoute>
            } />
            <Route path="/syndication/:id" element={
              <ProtectedRoute>
                <SyndicationDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness" element={
              <ProtectedRoute>
                <CRSDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness/score" element={
              <ProtectedRoute>
                <CRSScorerPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness/history" element={
              <ProtectedRoute>
                <CRSHistoryPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness/gaps" element={
              <ProtectedRoute>
                <CRSGapAnalysisPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness/providers" element={
              <ProtectedRoute>
                <ServiceProvidersPage />
              </ProtectedRoute>
            } />
            <Route path="/capital-readiness/scores/:id" element={
              <ProtectedRoute>
                <CRSDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/momentum" element={
              <ProtectedRoute>
                <MomentumDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/momentum/hot-deals" element={
              <ProtectedRoute>
                <HotDealsPage />
              </ProtectedRoute>
            } />
            <Route path="/momentum/:entityKind/:entityId" element={
              <ProtectedRoute>
                <MomentumDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/integrations" element={
              <ProtectedRoute>
                <IntegrationsPage />
              </ProtectedRoute>
            } />
            <Route path="/ai-settings" element={
              <ProtectedRoute>
                <AISettingsPage />
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            } />
            {/* Public: anyone with the token can accept the invite. */}
            <Route path="/invite/:token" element={<InviteAcceptPage />} />
            {/* Self-serve dashboards for external users (no team seat needed). */}
            <Route path="/investor/dashboard" element={
              <ProtectedRoute>
                <InvestorDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/founder/dashboard" element={
              <ProtectedRoute>
                <FounderDashboardPage />
              </ProtectedRoute>
            } />
            <Route path="/partner/network" element={
              <ProtectedRoute>
                <NetworkPartnerDashboardPage />
              </ProtectedRoute>
            } />
            {/* Public CRS preview from a founder-shared link. */}
            <Route path="/share/crs/:companyId" element={<PublicCRSPreviewPage />} />
            <Route path="/imports" element={
              <ProtectedRoute>
                <ImportsPage />
              </ProtectedRoute>
            } />
            <Route path="/imports/new" element={
              <ProtectedRoute>
                <NewImportPage />
              </ProtectedRoute>
            } />
            <Route path="/imports/new/business-cards" element={
              <ProtectedRoute>
                <BusinessCardImportPage />
              </ProtectedRoute>
            } />
            <Route path="/imports/:id" element={
              <ProtectedRoute>
                <ImportBatchDetailPage />
              </ProtectedRoute>
            } />
            <Route path="/settings/claude-usage" element={
              <ProtectedRoute>
                <ClaudeUsagePage />
              </ProtectedRoute>
            } />
            {/* TODO: Add AnalysisResults component
            <Route path="/analysis/:analysisId" element={
              <ProtectedRoute>
                <AnalysisResults />
              </ProtectedRoute>
            } /> */}
            {/* TODO: Add CompanyProfile component
            <Route path="/company/:companyId" element={
              <ProtectedRoute>
                <CompanyProfile />
              </ProtectedRoute>
            } /> */}
            {/* TODO: Add Settings component
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } /> */}

            {/* Redirect unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Global toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#fff',
                color: '#374151',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                borderRadius: '0.75rem',
                border: '1px solid #e5e7eb',
              },
              success: {
                iconTheme: {
                  primary: '#059669',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#dc2626',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>

      {/* React Query Dev Tools (only in development) */}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
};

export default App;
